import { SESSION_FORMATS, type SessionFormat } from "@klarify/constants";
import type { DirectoryFacetOptions } from "@klarify/types";
import { resolvePageParam } from "@klarify/utilities";
import type { TherapistDirectoryEntry } from "./therapist-directory-card";

export type SortOption = "recommended" | "name" | "newest";

const SORT_OPTIONS: readonly SortOption[] = ["recommended", "name", "newest"];

export const isSortOption = (value: string): value is SortOption =>
	(SORT_OPTIONS as readonly string[]).includes(value);

export type DirectoryFilters = {
	country: string | null;
	state: string | null;
	city: string | null;
	specialties: string[];
	formats: SessionFormat[];
	query: string;
	sort: SortOption;
	page: number;
};

const parseList = (value: string | null): string[] =>
	value
		? value
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean)
		: [];

const parseFormatList = (value: string | null): SessionFormat[] =>
	parseList(value).filter((v): v is SessionFormat =>
		(SESSION_FORMATS as readonly string[]).includes(v),
	);

const upperOrNull = (value: string | null): string | null =>
	value ? value.toUpperCase() : null;

const parseSort = (value: string | null): SortOption =>
	(SORT_OPTIONS as readonly string[]).includes(value ?? "")
		? (value as SortOption)
		: "recommended";

export function parseFilters(params: URLSearchParams): DirectoryFilters {
	return {
		country: upperOrNull(params.get("country")),
		state: upperOrNull(params.get("state")),
		city: params.get("city"),
		specialties: parseList(params.get("specialty")),
		formats: parseFormatList(params.get("format")),
		query: (params.get("q") ?? "").trim(),
		sort: parseSort(params.get("sort")),
		page: resolvePageParam(params.get("page")),
	};
}

export const statesForCountry = (
	options: DirectoryFacetOptions,
	country: string | null,
): string[] => (country ? (options.statesByCountry[country] ?? []) : []);

export const citiesForLocation = (
	options: DirectoryFacetOptions,
	country: string | null,
	state: string | null,
): string[] => {
	if (!country) {
		return [];
	}
	return (
		options.citiesByLocation[state ? `${country}:${state}` : country] ?? []
	);
};

// Given results already filtered by a state, split into in-person (the state is
// the profile's primary) vs remote (served via a non-primary telehealth row).
export function splitByDelivery(
	entries: TherapistDirectoryEntry[],
	state: string,
	country?: string | null,
): { inPerson: TherapistDirectoryEntry[]; remote: TherapistDirectoryEntry[] } {
	const inPerson: TherapistDirectoryEntry[] = [];
	const remote: TherapistDirectoryEntry[] = [];
	for (const e of entries) {
		const isPrimaryHere =
			!e.telehealthOnly &&
			e.jurisdictions.some(
				(j) =>
					j.isPrimary &&
					j.state === state &&
					(!country || j.country === country),
			);
		(isPrimaryHere ? inPerson : remote).push(e);
	}
	return { inPerson, remote };
}
